import { ApolloClient, ObservableQuery } from '@apollo/client'
import { BehaviorSubject, combineLatest, interval, Observable, switchMap } from 'rxjs'
import { catchError, filter, map } from 'rxjs/operators'

import {
  Exact,
  GetCustomerCartDocument,
  GetCustomerCartQuery,
  GetGuestCartDocument,
  GetGuestCartQuery,
} from '../../../graphql/magento'
import { isNotNull } from '../../../utils/collectionTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { fromObservableQuery } from '../../../utils/rx/observable-query'
import { latestAccessToken$ } from '../../auth/state'
import { latestApolloClient$ } from '../../graphql/apollo'

/**
 * Get observable query from Apollo client for customer's cart
 */
const getCustomerCartObservableQueryFactory = (
  client: ApolloClient<unknown>,
  token: string,
): ObservableQuery<GetCustomerCartQuery, Exact<{ [key: string]: never }>> =>
  client.watchQuery({
    query: GetCustomerCartDocument,
    context: { token },
    // override global error policy
    errorPolicy: 'none',
  })

const watchGetCustomerCartFactory = (client: ApolloClient<unknown>, token: string) =>
  fromObservableQuery(getCustomerCartObservableQueryFactory(client, token)).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server)
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    catchError(coerceCaughtToLeft),
  )

/**
 * Combine latest Apollo client with access token and return the customer's cart, watching for updates.
 *
 * NOTE: this does not support guest carts.
 */
export const latestCustomerCart$: Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery }
> = combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
  switchMap(([client, token]) => watchGetCustomerCartFactory(client, token)),
)

/**
 * Combine latest Apollo client with access token and refetch the customer's cart with the given interval.
 */
export const refetchCustomerCartWithInterval$ = (
  milliseconds: number,
): Observable<{ _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery }> =>
  combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
    switchMap(([client, token]) => {
      const obsQuery = getCustomerCartObservableQueryFactory(client, token)
      return interval(milliseconds).pipe(
        switchMap(() => obsQuery.refetch()),
        map(({ data }) => ({ _tag: 'Right' as const, data })),
        catchError(coerceCaughtToLeft),
      )
    }),
  )

/**
 * BehaviorSubject to track the current guest cart ID
 */
export const latestGuestCartId$ = new BehaviorSubject<string | null>(null)

/**
 * Update the guest cart ID observable
 */
export const updateGuestCartId = (cartId: string | null): void => {
  latestGuestCartId$.next(cartId)
}

/**
 * Get observable query from Apollo client for guest cart
 */
const getGuestCartObservableQueryFactory = (
  client: ApolloClient<unknown>,
  cartId: string,
): ObservableQuery<GetGuestCartQuery, Exact<{ cartId: string }>> =>
  client.watchQuery({
    query: GetGuestCartDocument,
    variables: { cartId },
    // override global error policy
    errorPolicy: 'none',
  })

/**
 * Combine latest Apollo client with guest cart ID and refetch the guest cart with the given interval.
 */
export const refetchGuestCartWithInterval$ = (
  milliseconds: number,
): Observable<{ _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetGuestCartQuery }> =>
  combineLatest([latestApolloClient$, latestGuestCartId$.pipe(filter(isNotNull))]).pipe(
    switchMap(([client, cartId]) => {
      const obsQuery = getGuestCartObservableQueryFactory(client, cartId)
      return interval(milliseconds).pipe(
        switchMap(() => obsQuery.refetch()),
        map(({ data }) => ({ _tag: 'Right' as const, data })),
        catchError(coerceCaughtToLeft),
      )
    }),
  )

/**
 * Universal cart refetch with interval that works for both customer and guest carts.
 * Uses customer cart refetch if access token is available, otherwise uses guest cart refetch.
 */
export const refetchCartWithInterval$ = (
  milliseconds: number,
): Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: GetCustomerCartQuery | GetGuestCartQuery }
> =>
  combineLatest([latestAccessToken$, latestGuestCartId$]).pipe(
    switchMap(([accessToken, guestCartId]) => {
      // If we have an access token, use customer cart refetch
      if (accessToken) {
        return refetchCustomerCartWithInterval$(milliseconds)
      }
      // If we have a guest cart ID, use guest cart refetch
      if (guestCartId) {
        return refetchGuestCartWithInterval$(milliseconds)
      }
      // If neither, return empty observable (no cart to refetch)
      return []
    }),
  )
